import React, { useEffect, useMemo } from 'react';

import { Box, Checkbox, Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { addressExists } from 'components/address-modal/utils';
import { ISaveDeliveryAddressInput, IsoCountryCode } from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { useOrderContext } from 'state/order';

const SaveAddressSelection: React.FC = () => {
  const { formatMessage } = useIntl();
  const { user, saveDeliveryAddress } = useAuthContext();
  const {
    deliveryAddress,
    orderPhoneNumber,
    updateShouldSaveDeliveryAddress,
    setShouldSaveDeliveryAddress,
    formShouldSaveDeliveryAddress,
    setFormShouldSaveDeliveryAddress,
  } = useOrderContext();

  const isAuthenticated = Boolean(user?.cognitoId && user?.details);

  const { allAdress, isUsingSavedAddress, isAddressExisting } = useMemo(() => {
    const addresses = user?.details?.deliveryAddresses || [];
    if (!deliveryAddress) {
      return { isUsingSavedAddress: false, isAddressExisting: false };
    }

    const enrichedAddress = {
      ...deliveryAddress,
      phoneNumber: orderPhoneNumber,
    };

    const found = addressExists(addresses, enrichedAddress);
    return {
      allAdress: addresses,
      isUsingSavedAddress: found,
      isAddressExisting: found,
    };
  }, [deliveryAddress, orderPhoneNumber, user?.details?.deliveryAddresses]);

  useEffect(() => {
    if (
      user?.cognitoId &&
      deliveryAddress &&
      formShouldSaveDeliveryAddress &&
      allAdress?.length === 0
    ) {
      const SaveDeliveryAddressInput: ISaveDeliveryAddressInput = {
        addressLine1: deliveryAddress?.addressLine1,
        addressLine2: deliveryAddress?.addressLine2,
        alias: deliveryAddress?.alias,
        city: deliveryAddress?.city,
        state: deliveryAddress?.state,
        zip: deliveryAddress?.zip,
        country: deliveryAddress?.country as IsoCountryCode,
        phoneNumber: deliveryAddress?.phoneNumber ?? orderPhoneNumber,
        defaultInstruction: deliveryAddress?.defaultInstruction,
        route: deliveryAddress?.route,
        streetNumber: deliveryAddress?.streetNumber,
        latitude: deliveryAddress?.latitude,
        longitude: deliveryAddress?.longitude,
      };

      saveDeliveryAddress(SaveDeliveryAddressInput);
    }

    if (isUsingSavedAddress) {
      updateShouldSaveDeliveryAddress(false);
      setShouldSaveDeliveryAddress(false);
    }

    if (!isAuthenticated) {
      updateShouldSaveDeliveryAddress(false);
      setShouldSaveDeliveryAddress(false);
      return;
    }

    if (!isAddressExisting && formShouldSaveDeliveryAddress) {
      updateShouldSaveDeliveryAddress(true);
      setShouldSaveDeliveryAddress(true);
    }
  }, [
    isUsingSavedAddress,
    isAddressExisting,
    formShouldSaveDeliveryAddress,
    updateShouldSaveDeliveryAddress,
    setFormShouldSaveDeliveryAddress,
    setShouldSaveDeliveryAddress,
    isAuthenticated,
    user?.cognitoId,
    deliveryAddress,
    allAdress?.length,
    orderPhoneNumber,
    saveDeliveryAddress,
  ]);

  const handleSaveAddressClick = () => {
    const newValue = !formShouldSaveDeliveryAddress;
    setFormShouldSaveDeliveryAddress(newValue);

    if (isAuthenticated) {
      updateShouldSaveDeliveryAddress(newValue);
      setShouldSaveDeliveryAddress(newValue);
    }
  };

  return (
    <Checkbox
      testID="save-address-selection"
      isChecked={formShouldSaveDeliveryAddress ?? deliveryAddress?.shouldSave}
      onChange={handleSaveAddressClick}
      name="checkbox-save-address-selection"
      value="checkbox-save-address-selection"
      isDisabled={isUsingSavedAddress || isAddressExisting}
    >
      <Box paddingX="$2" flex="1">
        <Text variant="copyTwo">
          {formatMessage(
            { id: 'saveAddressLabel' },
            { disclaimer: formatMessage({ id: 'saveAddressDisclaimer' }) }
          )}
        </Text>
      </Box>
    </Checkbox>
  );
};

export default SaveAddressSelection;
